import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { createClient } from '../../services/adminService';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';
import NafCodeSelector from '../shared/NafCodeSelector';
import SelectedNafCodes from '../shared/SelectedNafCodes';

import { User, Company } from '../../services/adminService';

interface CreateClientPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onClientCreated: (data: { user: User; company: Company }) => void;
}

export default function CreateClientPopup({ isOpen, onClose, onClientCreated }: CreateClientPopupProps) {
  // State for form step
  const [step, setStep] = useState<'company' | 'user'>('company');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State for company form
  const [companyData, setCompanyData] = useState<Partial<Company>>({
    name: '',
    siret: '',
    email: '',
    phone: '',
    address: '',
    postalCode: '',
    city: '',
    departement: '',
    country: 'France',
    website: '',
    nafCodes: [],
  });
  
  // State for user form
  const [userData, setUserData] = useState<Partial<User> & { password: string; confirmPassword: string }>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'ROLE_USER',
    isActive: true,
  });
  
  // State for form validation
  const [companyErrors, setCompanyErrors] = useState<Record<string, string>>({});
  const [userErrors, setUserErrors] = useState<Record<string, string>>({});

  // Validate company form
  const validateCompanyForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!companyData.name?.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (!companyData.siret?.trim()) {
      newErrors.siret = 'Le SIRET est requis';
    } else if (!/^\d{14}$/.test(companyData.siret)) {
      newErrors.siret = 'Le SIRET doit contenir 14 chiffres';
    }
    
    if (companyData.email && !/\S+@\S+\.\S+/.test(companyData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    setCompanyErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate user form
  const validateUserForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!userData.firstName?.trim()) {
      newErrors.firstName = 'Le prénom est requis';
    }
    
    if (!userData.lastName?.trim()) {
      newErrors.lastName = 'Le nom est requis';
    }
    
    if (!userData.email?.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(userData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    if (!userData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (userData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }
    
    if (userData.password !== userData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    setUserErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Reset all forms
  const resetForms = () => {
    setCompanyData({
      name: '',
      siret: '',
      email: '',
      phone: '',
      address: '',
      postalCode: '',
      city: '',
      departement: '',
      country: 'France',
      website: '',
      nafCodes: [],
    });
    
    setUserData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'ROLE_USER',
      isActive: true,
    });
    
    setStep('company');
    
    setCompanyErrors({});
    setUserErrors({});
  };

  // Handle company form field changes
  const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCompanyData({
      ...companyData,
      [name]: value,
    });
  };

  // Handle user form field changes
  const handleUserChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setUserData({
        ...userData,
        [name]: checked,
      });
    } else {
      setUserData({
        ...userData,
        [name]: value,
      });
    }
  };

  // Handle adding NAF code
  const handleNafCodesChange = (codes: string[]) => {
    setCompanyData({
      ...companyData,
      nafCodes: codes,
    });
  };

  // Handle removing NAF code
  const handleRemoveNafCode = (codeToRemove: string) => {
    const updatedNafCodes = (companyData.nafCodes || []).filter(code => code !== codeToRemove);
    setCompanyData({
      ...companyData,
      nafCodes: updatedNafCodes,
    });
  };

  // Handle next button in company form
  const handleNextStep = () => {
    if (validateCompanyForm()) {
      setStep('user');
    }
  };

  // Handle back button in user form
  const handleBackStep = () => {
    setStep('company');
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateUserForm()) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Create user data object without confirmPassword
      const userDataToSend = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        password: userData.password,
        role: userData.role,
        isActive: userData.isActive
      };
      
      const result = await createClient({
        company: companyData,
        user: userDataToSend,
      });
      
      onClientCreated(result);
      showSuccessToast('Client créé avec succès', `client-created-${result.user.id}`);
      
      // Reset form
      resetForms();
      
      onClose();
    } catch (error) {
      console.error('Error creating client:', error);
      showErrorToast('Erreur lors de la création du client', 'client-create-error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render company form
  const renderCompanyForm = () => (
    <div>
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
          <div className="sm:col-span-6">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Nom de l'entreprise *
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="name"
                id="name"
                value={companyData.name || ''}
                onChange={handleCompanyChange}
                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                  companyErrors.name 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
              />
              {companyErrors.name && (
                <p className="mt-1 text-sm text-red-600">{companyErrors.name}</p>
              )}
            </div>
          </div>

          <div className="sm:col-span-6">
            <label htmlFor="siret" className="block text-sm font-medium text-gray-700">
              SIRET *
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="siret"
                id="siret"
                value={companyData.siret || ''}
                onChange={handleCompanyChange}
                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                  companyErrors.siret 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
              />
              {companyErrors.siret && (
                <p className="mt-1 text-sm text-red-600">{companyErrors.siret}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">14 chiffres sans espaces</p>
            </div>
          </div>

          <div className="sm:col-span-3">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <div className="mt-1">
              <input
                type="email"
                name="email"
                id="email"
                value={companyData.email || ''}
                onChange={handleCompanyChange}
                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                  companyErrors.email 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
              />
              {companyErrors.email && (
                <p className="mt-1 text-sm text-red-600">{companyErrors.email}</p>
              )}
            </div>
          </div>

          <div className="sm:col-span-3">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
              Téléphone
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="phone"
                id="phone"
                value={companyData.phone || ''}
                onChange={handleCompanyChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="sm:col-span-6">
            <label htmlFor="address" className="block text-sm font-medium text-gray-700">
              Adresse
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="address"
                id="address"
                value={companyData.address || ''}
                onChange={handleCompanyChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
              Code postal
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="postalCode"
                id="postalCode"
                value={companyData.postalCode || ''}
                onChange={handleCompanyChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="city" className="block text-sm font-medium text-gray-700">
              Ville
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="city"
                id="city"
                value={companyData.city || ''}
                onChange={handleCompanyChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="departement" className="block text-sm font-medium text-gray-700">
              Département
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="departement"
                id="departement"
                value={companyData.departement || ''}
                onChange={handleCompanyChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">ex: 75, 06, ...</p>
          </div>

          <div className="sm:col-span-6">
            <label htmlFor="nafCodes" className="block text-sm font-medium text-gray-700">
              Codes NAF
            </label>
            <div className="mt-1">
              <NafCodeSelector
                selectedNafCodes={companyData.nafCodes || []}
                onChange={handleNafCodesChange}
                disabled={isSubmitting}
                placeholder="Rechercher un code NAF..."
                label=""
              />
              
              <SelectedNafCodes 
                selectedCodes={companyData.nafCodes || []}
                onRemove={handleRemoveNafCode}
                className="mt-2"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            onClick={handleNextStep}
            className="inline-flex w-full justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Suivant
          </button>
          <button
            type="button"
            className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
            onClick={onClose}
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );

  // Render user form
  const renderUserForm = () => (
    <form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
          <div className="sm:col-span-3">
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
              Prénom *
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="firstName"
                id="firstName"
                value={userData.firstName}
                onChange={handleUserChange}
                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                  userErrors.firstName 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
              />
              {userErrors.firstName && (
                <p className="mt-1 text-sm text-red-600">{userErrors.firstName}</p>
              )}
            </div>
          </div>

          <div className="sm:col-span-3">
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
              Nom *
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="lastName"
                id="lastName"
                value={userData.lastName}
                onChange={handleUserChange}
                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                  userErrors.lastName 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
              />
              {userErrors.lastName && (
                <p className="mt-1 text-sm text-red-600">{userErrors.lastName}</p>
              )}
            </div>
          </div>

          <div className="sm:col-span-6">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Adresse email *
            </label>
            <div className="mt-1">
              <input
                type="email"
                name="email"
                id="email"
                value={userData.email}
                onChange={handleUserChange}
                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                  userErrors.email 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
              />
              {userErrors.email && (
                <p className="mt-1 text-sm text-red-600">{userErrors.email}</p>
              )}
            </div>
          </div>

          <div className="sm:col-span-3">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Mot de passe *
            </label>
            <div className="mt-1">
              <input
                type="password"
                name="password"
                id="password"
                value={userData.password}
                onChange={handleUserChange}
                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                  userErrors.password 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
              />
              {userErrors.password && (
                <p className="mt-1 text-sm text-red-600">{userErrors.password}</p>
              )}
            </div>
          </div>

          <div className="sm:col-span-3">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirmer le mot de passe *
            </label>
            <div className="mt-1">
              <input
                type="password"
                name="confirmPassword"
                id="confirmPassword"
                value={userData.confirmPassword}
                onChange={handleUserChange}
                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                  userErrors.confirmPassword 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
              />
              {userErrors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{userErrors.confirmPassword}</p>
              )}
            </div>
          </div>

          <div className="sm:col-span-3">
            <label htmlFor="role" className="block text-sm font-medium text-gray-700">
              Rôle
            </label>
            <div className="mt-1">
              <select
                id="role"
                name="role"
                value={userData.role}
                onChange={handleUserChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="ROLE_USER">Utilisateur</option>
                <option value="ROLE_ADMIN">Administrateur</option>
              </select>
            </div>
          </div>

          <div className="sm:col-span-3">
            <div className="flex h-full items-center">
              <div className="relative flex items-start">
                <div className="flex h-5 items-center">
                  <input
                    id="isActive"
                    name="isActive"
                    type="checkbox"
                    checked={userData.isActive}
                    onChange={handleUserChange}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="isActive" className="font-medium text-gray-700">
                    Compte actif
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 sm:flex sm:flex-row-reverse">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex w-full justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
          >
            {isSubmitting ? 'Création en cours...' : 'Créer'}
          </button>
          <button
            type="button"
            className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
            onClick={handleBackStep}
          >
            Retour
          </button>
          <button
            type="button"
            className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
            onClick={onClose}
          >
            Annuler
          </button>
        </div>
      </div>
    </form>
  );

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Fermer</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div>
                  <div className="mt-3 text-center sm:mt-0 sm:text-left">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {step === 'company' ? 'Étape 1: Créer une entreprise' : 'Étape 2: Créer un utilisateur'}
                    </Dialog.Title>
                    <div className="mt-4">
                      {step === 'company' ? renderCompanyForm() : renderUserForm()}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
} 